import React from 'react';
import useSWR from 'swr';
import { WP_REST_API_Post } from 'wp-types';
import { Box, makeStyles } from '@material-ui/core';
import { productionWordpressBlogFetcher } from '@api/rest';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { WP_FEATUREDMEDIA, WP_TERM } from './type';

interface CompanyBlogPreviewPresenterProps {
  blogURL: string;
}

const CompanyBlogPreviewPresenter = ({
  blogURL
}: CompanyBlogPreviewPresenterProps) => {
  const slug = blogURL.split('post/')[1];
  const { data, error } = useSWR<{ data: WP_REST_API_Post[] }>(
    `/wp/v2/posts?_embed&slug=${slug}`,
    productionWordpressBlogFetcher
  );
  const { root, body2, body1 } = useStyles();

  if (!error && !data) {
    return <>Loading..</>;
  }

  if (error || !data?.data.length) {
    return null;
  }

  const { title, imgData, excerpt } = extractToData(data!.data[0]);

  return (
    <Box
      className={root}
      width={'100%'}
      bgcolor={colorSet.gray100}
      border={`1px solid ${colorSet.gray300}`}
      borderRadius={12}
      padding={'12px 20px 16px'}
      onClick={() => window.open(blogURL)}
    >
      <Box marginBottom={1}>
        <CustomizedTypography variant="body2">
          캐파 블로그 링크
        </CustomizedTypography>
      </Box>
      <Box display={'flex'} alignItems={'center'}>
        <Box
          minHeight={48}
          minWidth={64}
          marginRight={'12px'}
          borderRadius={8}
          overflow={'hidden'}
        >
          <img
            width={64}
            height={48}
            src={imgData.source_url}
            alt={imgData.alt_text}
          />
        </Box>

        <Box width={'calc(100% - 64px)'}>
          <Box>
            <Box
              className={body1}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          </Box>
          <Box
            className={body2}
            dangerouslySetInnerHTML={{ __html: excerpt }}
          ></Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyBlogPreviewPresenter;

const extractToData = (blogData: WP_REST_API_Post) => {
  const title = blogData.title.rendered;
  const embed = blogData._embedded as WP_REST_API_Post['_embedded'];
  const wp_featured = embed?.['wp:featuredmedia']?.[0] as WP_FEATUREDMEDIA;
  const imgData = wp_featured
    ? wp_featured
    : { source_url: '', alt_text: 'CAPA_LOGO' };
  const wp_term = embed?.['wp:term']?.[0] as WP_TERM[];
  const category =
    wp_term.length > 1
      ? wp_term[0].id < wp_term[1].id
        ? wp_term[1].name
        : wp_term[0].name
      : wp_term[0].name;
  const excerpt = blogData.excerpt.rendered.replace('[&hellip;]', '...');

  return { title, imgData, category, excerpt };
};

const useStyles = makeStyles(() => ({
  root: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colorSet.gray300
    }
  },
  body1: {
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: -0.1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  body2: {
    '& > p': {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: `20.02px`,
      letterSpacing: -0.1,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  }
}));
